/**
 * useEingangComm - the Eingang itself, for an Eingang that has no task.
 *
 * Teil 1 normally reads the mail out of the task split view, which leaves an unrouted
 * upload (8.1's "Route zu Kunde" case) showing nothing. Only fetched when there are
 * zero tasks.
 *
 * Mock: the POSTEINGANG_MOCK row, which carries every Teil-1 fact. Live: GET
 * /communications/:id/content, a CONTENT projection - subject and body only, so Kanal,
 * from/to and received_at come back null rather than invented. The full set needs
 * GET /posteingang/:id (B1).
 */

import { useQuery } from '@tanstack/react-query';
import type { CommunicationView, CommVisibilityTier } from '@brokly/shared';

/** The Eingang plus the caller's tier, which the content route resolves and the mask words the empty body by. */
export type EingangComm = CommunicationView & { your_effective_tier?: CommVisibilityTier | null };
import { apiClient } from '../../../lib/api-client';
import { isLiveData } from '../../../lib/data-mode';
import { qk } from '../../../lib/query-keys';
import { POSTEINGANG_MOCK } from '../mock';

/** The Posteingang row → the Teil-1 shape. Every field is one the row already carries. */
function fromPosteingangMock(commId: string): EingangComm | null {
  const item = POSTEINGANG_MOCK.find((i) => i.id === commId);
  if (!item) return null;
  return {
    id: item.id,
    // The list's Kanal is already collapsed (POSTEINGANG_KANAL_SQL); map it back to the
    // `communication.channel` vocabulary EingangMailView re-collapses, so mock and live
    // hand Teil 1 the same kind of value.
    channel: item.kanal === 'scanning_center' ? 'post' : item.kanal === 'portal' ? 'portal' : 'email',
    direction: 'inbound',
    subject: item.subject,
    from: item.from,
    to: item.to,
    cc: item.cc,
    message_id: null,
    received_at: item.received_at,
    body:
      'Guten Tag\n\nbeiliegend die Unterlagen zu diesem Eingang.\n\nFreundliche Grüsse',
    body_html: null,
  };
}

/**
 * The Eingang behind `commId`. Only fetched when asked for - the Maske enables this
 * ONLY when the task split view produced no communication, so the normal path stays
 * exactly one request.
 */
export function useEingangComm(commId: string | null, enabled: boolean) {
  const live = isLiveData();
  return useQuery<EingangComm | null>({
    queryKey: qk.eingangComm(commId ?? 'none', live ? 'live' : 'mock'),
    queryFn: async () => {
      if (commId === null) return null;
      if (!live) return fromPosteingangMock(commId);
      const content = await apiClient.communications.getContent(commId);
      return {
        id: content.communication_id,
        // Not on the content DTO — see the header. Teil 1 renders what it has.
        channel: 'email',
        direction: 'inbound',
        subject: content.subject,
        from: null,
        to: null,
        cc: null,
        message_id: null,
        received_at: new Date().toISOString(),
        body: content.body,
        body_html: content.body_html,
        your_effective_tier: content.your_effective_tier,
      };
    },
    enabled: enabled && commId !== null,
    retry: false,
    ...(live ? {} : { staleTime: Infinity }),
  });
}
